// Menu lateral, montado pelo servidor.
//
// A estrutura era cinco arrays em `AppSidebar.tsx`, com um `if (isSuperuser)`
// como único filtro. Todo item chegava ao navegador: esconder no React esconde
// do olho, não do DevTools — quem abre o inspetor lê a lista inteira, rota e
// rótulo de tudo que existe, inclusive a área administrativa.
//
// Agora vem de `menu_do_usuario()`. A 0036 filtrava por permissão e não
// enviava o que o papel não alcança; a 0041 passou a enviar tudo com
// `permitido`, e o portal desabilita o que for falso — menu vazio não informa
// nada, menu cinza informa que a tela existe e o acesso não.
//
// O que continua **não** sendo enviado é a área de superusuário: ela não é do
// Tenant, e o nome dos clientes não tem por que existir na tela de quem
// contratou o produto.
//
// **Isto é ocultação, não autorização.** Quem souber a rota continua digitando
// `/admin/clientes` no navegador. O que barra é a RLS e a checagem na própria
// tela, que continuam onde estão.
//
// Não passa pelo catálogo de configuração: `useCatalogo` guarda em cache de
// sessão o que é **global**, e o menu é por usuário. Cachear resposta de um
// usuário numa chave compartilhada seria servir o menu do superusuário para
// quem entrasse depois.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface ItemDeMenu {
  id: string;
  rotulo: string;
  icone: string;
  /** Nulo nas abas do condomínio: a rota depende do condomínio aberto. */
  href: string | null;
  /**
   * O usuário alcança este item? (migration 0041)
   *
   * Opcional porque o banco pode ainda estar na 0036, que filtrava os itens em
   * vez de marcá-los. Ausente é lido como permitido — era o contrato antigo:
   * o que chegava, chegava porque podia.
   */
  permitido?: boolean;
}

export interface ItemDeMenuRaiz extends ItemDeMenu {
  filhos: ItemDeMenu[];
}

export interface Menu {
  principal: ItemDeMenuRaiz[];
  rodape: ItemDeMenuRaiz[];
  condominio: ItemDeMenuRaiz[];
}

const MENU_VAZIO: Menu = { principal: [], rodape: [], condominio: [] };

/**
 * Menu do usuário autenticado.
 *
 * `effectiveAccountId` entra só como gatilho de recarga: quando o superusuário
 * impersona outra conta, o menu é reavaliado — a função é `security invoker` e
 * responde pelo que a sessão permite.
 *
 * Falha devolve menu vazio em vez de menu completo. Um menu que aparece porque
 * a consulta quebrou seria exatamente o que este módulo existe para evitar.
 */
export function useMenu(effectiveAccountId?: string): {
  menu: Menu;
  carregando: boolean;
  erro: string | null;
} {
  const [menu, setMenu] = useState<Menu>(MENU_VAZIO);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const { data, error } = await supabase.rpc('menu_do_usuario');

    if (error) {
      setErro(error.message);
      setMenu(MENU_VAZIO);
    } else {
      setErro(null);
      setMenu({ ...MENU_VAZIO, ...(data as Menu) });
    }
    setCarregando(false);
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar, effectiveAccountId]);

  return { menu, carregando, erro };
}

/** O item existe no menu deste usuário? Use para decidir rota, não acesso. */
export function temItem(menu: Menu, id: string): boolean {
  const procurar = (itens: ItemDeMenuRaiz[]): boolean =>
    itens.some((i) => i.id === id || i.filhos.some((f) => f.id === id));
  return procurar(menu.principal) || procurar(menu.rodape) || procurar(menu.condominio);
}

/** Sufixo do id das abas do condomínio: `condominio.torres` → `torres`. */
export const abaDoCondominio = (id: string) => id.replace(/^condominio\./, '');
